export const GUIDE_STEPS = [
    {
        title: "Strategic Provider Selection: Beyond Basic Comparisons",
        content:
            "International money transfers have evolved dramatically in recent years. Gone are the days of expensive, slow bank-to-bank transfers. Today's digital landscape offers multiple options that can save you money, time, and frustration. When choosing a money transfer service, look deeper than surface-level rates. Consider these nuanced factors:",
        bullets: ["Transfer Infrastructure: Evaluate providers with robust global networks", "Compliance & Security: Prioritize services with strong regulatory compliance", "Customer Support Quality: Check responsiveness and multilingual support", "Technology Integration: Prefer platforms with user-friendly mobile apps and real-time tracking"],
    },
    {
        title: "Advanced Identity Verification Insights",
        content:
            "Modern remittance platforms have sophisticated verification processes:",
        bullets: ["Digital ID Verification: Many now offer instant online verification using advanced document scanning", "Biometric Authentication: Some cutting-edge services incorporate facial recognition or fingerprint validation", "Encrypted Personal Data: Ensure the platform uses bank-grade encryption for your sensitive information"],
    },
    {
        title: "Precision in Transfer Details",
        content:
            "Modern transfer services require thorough identity verification. This isn't just bureaucracy—it's a critical step in preventing fraud and ensuring secure transactions. Be prepared to provide government-issued identification and potentially additional documentation. Accuracy is your financial safeguard. Implement these meticulous practices:",
        bullets: ["Use official, full legal names matching government documentation", "Verify recipient banking information through multiple sources", "Consider creating a secure contact list within the transfer platform", "Use standardized international banking codes (SWIFT/IBAN) for precision"],
    },
    {
        title: "Payment Method Optimization",
        content:
            "Every transfer method comes with its own set of advantages and considerations. Traditional banks, dedicated money transfer services, digital wallets, and emerging fintech platforms each offer unique benefits. The key is finding the right solution for your specific needs. Each payment channel carries unique advantages:",
        bank_transfer: {
            title: "Bank Transfers",
            bullets: ["Lower fees", "Slower processing", "High security", "Best for larger amounts"]
        },
        credit_cards: {
            title: "Credit/Debit Cards",
            bullets: ["Instant processing", "Higher convenience fees", "Immediate availability", "Ideal for urgent transfers"]
        },
        digital_wallets: {
            title: "Digital Wallets",
            bullets: ["Emerging technology", "Often lower fees", "Enhanced tracking capabilities", "Growing global acceptance"]
        }
    },
    {
        title: "Risk Mitigation Strategies",
        content:
            "The most important aspect of any international transfer is ensuring your money reaches its destination safely. Look for providers regulated by financial authorities, with robust security protocols and a proven track record of successful transfers. Protect your financial transaction through proactive measures:",
        bullets: ["Always use two-factor authentication", "Set transfer limits", "Enable transaction alerts", "Keep detailed transfer documentation", "Understand your platform's fraud protection policies"],
    },
    {
        title: "Real-Time Transfer Monitoring",
        content:
            "Transfer times vary significantly between providers. Some services offer near-instant transfers, while others may take several business days. Consider your urgency and the recipient's needs when choosing a service. Modern technology enables unprecedented transfer transparency:",
        bullets: ["Use mobile apps with live tracking", "Set up SMS or email notifications", "Understand typical transfer windows for different regions", "Keep transaction reference numbers accessible"],
    },
    {
        title: "Post-Transfer Best Practices",
        content:
            "Your responsibility doesn't end when you hit 'send':",
        bullets: ["Confirm receipt with your recipient", "Save all transaction receipts", "Report any discrepancies immediately", "Provide feedback to improve platform services"],
        content: "Disclaimer: Always conduct your own research and consult financial advisors for personalized guidance.",
    },
];

export const FAQ_DATA = [
    {
        id: 1,
        question: "How do I send money using MoneyRateFinder?",
        answer:
            "Sending money is simple and quick. Just sign up, enter the recipient's details, choose the amount and payment method, review the fees and exchange rate, and confirm the transfer. You can track the transaction in real time.",
    },
    {
        id: 2,
        question: "What are the available transfer methods?",
        answer: "We offer multiple ways to send money, including:",
        bulletPoints: [
            "Bank Transfers – Directly to the recipient's bank account.",
            "Mobile Wallets – Transfer funds instantly to digital wallets.",
            "Cash Pickup – Recipients can collect cash at partnered locations.",
        ],
    },
    {
        id: 3,
        question: "How long does it take for my money to reach the recipient?",
        answer:
            "Most transactions are processed within minutes. However, bank deposits may take 1-2 business days, depending on the recipient's country and banking network.",
    },
    {
        id: 4,
        question: "How much are the transfer fees?",
        answer:
            "Fees depend on the destination, transfer amount, and payment method. You'll always see the full cost before confirming your transfer—no hidden charges.",
    },
    {
        id: 5,
        question: "What exchange rate will I get?",
        answer:
            "We offer competitive exchange rates that update in real time. The rate displayed at the time of transfer is the rate your recipient will receive.",
    },
    {
        id: 6,
        question: "Can I cancel or modify a transfer after sending?",
        answer:
            "If the transaction is still in progress, you may be able to cancel or modify it. However, once processed, it cannot be reversed. Contact customer support for immediate assistance.",
    },
    {
        id: 7,
        question: "What if my recipient doesn't receive the money?",
        answer:
            "If the transfer hasn't arrived within the expected timeframe, verify the recipient's details and check your transaction status. If the issue persists, reach out to our support team for assistance.",
    },
    {
        id: 8,
        question: "Is it safe to send money through MoneyRateFinder?",
        answer:
            "Yes. We use advanced encryption, fraud detection, and regulatory compliance to ensure every transaction is secure. Your funds and personal data are fully protected.",
    },
    {
        id: 9,
        question: "What documents do I need for verification?",
        answer:
            "To comply with global regulations, we may ask for a government-issued ID, proof of address, or other KYC (Know Your Customer) documents, depending on the amount and destination country.",
    },
    {
        id: 10,
        question: "How can I contact customer support?",
        answer:
            "Our support team is available 24/7 via live chat, email, and phone. You can also check our Help Center for quick solutions.",
    },
];

export const ANALYSIS_DATA = [
    {
        id: 1,
        title: "Real Cost Comparisons",
        description:
            "We analyze total costs, including hidden fees and exchange rate markups, so you always know the true price.",
        icon: "📊",
    },
    {
        id: 2,
        title: "More Providers, Better Deals",
        description:
            "We go beyond big names, comparing fintech disruptors and niche services to get you lower fees and better rates.",
        icon: "🔍",
    },
    {
        id: 3,
        title: "AI-Powered Personalization",
        description:
            "Our smart algorithm matches providers based on your amount, destination, and urgency for a perfect fit.",
        icon: "🤖",
    },
];
